import { addDaysISO } from '../../../../utils';

// The daily view loads messages in segments: date ranges that are each paged on their own. Only the
// two outermost segments ever fetch (the next page of the edge segment, or a new segment beyond it)
// and only while the user is scrolling towards that edge. Everything in this file is pure so that the
// paging rules can be tested without rendering the grid.
//
// Two ways to use them:
// - Empty dates shown: closed segments. The list walks through time, so a new segment is added
//   beyond the edge when the edge segment has no more pages.
// - Empty dates hidden: one open ended segment either side of the focus date (see createOpenSegments).
//   The date range never moves, only the paging does, so the list is a plain infinite list of messages.

export const SEGMENT_DAYS = 14;
export const MAX_SEGMENTS = 6;
// Messages between the viewport and a gap in an open list at which the dropped pages are fetched back.
// Counted in messages rather than days or pixels, because that is what the list is made of.
export const REFILL_ROWS = 30;
// With empty dates shown, the most messages the closed segments hold before pages are trimmed from the far
// end of the list. This bounds what a busy account holds, where MAX_SEGMENTS bounds the dates a quiet one does.
export const ROW_BUDGET = 500;
// Pages are only trimmed when at least this many loaded messages lie between the viewport and the page.
export const PROTECT_ROWS = 100;

export type SegmentSide = 'past' | 'future';

export type SegmentQueryState = {
  segment: CalendarSegment;
  isSuccess: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  rowCount: number;
  // Closed segments only: the pages held, and the messages and date of the last one. Pages are trimmed from the end.
  pageCount?: number;
  lastPageRows?: number;
  // The date in the last page that is nearest the focus date.
  lastPageNearDate?: string;
  // Earliest and latest day that has a loaded message.
  firstDate?: string;
  lastDate?: string;
  // Open ended segments only: the page numbers at either end of the pages held, and whether pages
  // before the first one were dropped to keep the list short.
  firstPageParam?: number;
  lastPageParam?: number;
  hasPreviousPage?: boolean;
};

export type BoundedSegment = CalendarSegment & { from: CalendarMonthString; to: CalendarMonthString };

export const isBounded = (segment: CalendarSegment): segment is BoundedSegment => segment.from !== null && segment.to !== null;

export type EdgeAction = { type: 'wait' } | { type: 'fetchNextPage' } | { type: 'append'; segment: CalendarSegment } | { type: 'end' };

// Whether the pages of a segment run away from the focus date on this side of the list: older segments on the
// past side and newer segments on the future side.
const goesOutwards = (segment: CalendarSegment, side: SegmentSide) => segment.direction === (side === 'past' ? 'older' : 'newer');

export const segmentKey = (segment: CalendarSegment) => `${segment.from}_${segment.to}`;

export const createInitialSegments = (focus: CalendarMonthString): BoundedSegment[] => [
  { from: addDaysISO(focus, -SEGMENT_DAYS), to: addDaysISO(focus, -1), direction: 'older' },
  { from: focus, to: addDaysISO(focus, SEGMENT_DAYS - 1), direction: 'newer' },
];

// Open ended on the outside: the past edge has no start and the future edge has no end.
export const createOpenSegments = (focus: CalendarMonthString): CalendarSegment[] => [
  { from: null, to: addDaysISO(focus, -1), direction: 'older' },
  { from: focus, to: null, direction: 'newer' },
];

export const daysBetween = (from: CalendarMonthString, to: CalendarMonthString): CalendarMonthString[] => {
  const days: CalendarMonthString[] = [];
  let cursor = from;

  while (cursor <= to) {
    days.push(cursor);
    cursor = addDaysISO(cursor, 1);
  }

  return days;
};

// The dates a list of closed segments spans, or null when an end is open.
export const segmentsRange = (segments: CalendarSegment[]) => {
  const from = segments[0]?.from;
  const to = segments[segments.length - 1]?.to;

  return from && to ? { from, to } : null;
};

// The dates a list of closed segments actually knows about, in order. A segment that is still on its
// first page knows none, and one that has only some of its pages knows the dates those pages reach,
// so a date is never shown as empty before its messages have had a chance to arrive. Older segments
// load their newest page first and newer segments their oldest page first.
export const loadedDays = (states: SegmentQueryState[]): CalendarMonthString[] =>
  states.flatMap((state) => {
    const { segment } = state;

    if (!state.isSuccess || !isBounded(segment)) return [];

    let { from, to } = segment;

    if (state.hasNextPage) {
      if (segment.direction === 'older') {
        if (state.firstDate === undefined) return [];
        if (state.firstDate > from) from = state.firstDate as CalendarMonthString;
      } else {
        if (state.lastDate === undefined) return [];
        if (state.lastDate < to) to = state.lastDate as CalendarMonthString;
      }
    }

    return from <= to ? daysBetween(from, to) : [];
  });

// With empty dates shown, a date can be scrolled to when its segment has loaded it. A segment that has not
// answered yet counts, so a jump that has just started a new list is not started again while it waits.
// A segment with more pages only covers the dates its pages have reached, which includes pages that were
// trimmed from its far end.
export const isDateLoadable = (states: SegmentQueryState[], iso: CalendarMonthString) => {
  const bounded = states.filter((state) => isBounded(state.segment));

  if (bounded.length !== states.length || states.length === 0) return true;

  const state = states.find((s) => iso >= (s.segment.from as string) && iso <= (s.segment.to as string));

  if (!state) return false;
  if (!state.isSuccess || !state.hasNextPage) return true;

  return state.segment.direction === 'older'
    ? state.firstDate !== undefined && iso >= state.firstDate
    : state.lastDate !== undefined && iso <= state.lastDate;
};

const toDateOnly = (sendDateLocalized: string) => sendDateLocalized.split('T')[0];

// With empty dates hidden the two edge segments split the timeline at the focus date and between them
// cover all of it. A date is covered when it lies on a side that has loaded down to it, or that has
// nothing further to load. A side that is still loading counts as covering it, so a jump that has just
// started a new list is not started again while it waits.
export const isDateCoveredOpen = (states: SegmentQueryState[], iso: CalendarMonthString) => {
  const past = states[0];
  const future = states[states.length - 1];

  if (!past || !future) return true;

  const splitAt = past.segment.to;
  const onPastSide = splitAt !== null && iso <= splitAt;
  const side = onPastSide ? past : future;

  if (!side.isSuccess) return true;

  const { firstDate, lastDate } = side;
  // Past pages run newest first and future pages oldest first, so the end that has more to load is
  // the far one, and the end that may have dropped pages is the one next to the focus date.
  const farEndCovered = !side.hasNextPage || (onPastSide ? firstDate !== undefined && iso >= firstDate : lastDate !== undefined && iso <= lastDate);
  const nearEndCovered =
    !side.hasPreviousPage || (onPastSide ? lastDate !== undefined && iso <= lastDate : firstDate !== undefined && iso >= firstDate);

  return farEndCovered && nearEndCovered;
};

// How far a request has got on one side. With an open list that is the last page held, which is not
// the same as the number of rows: loading a page at one end drops a page at the other.
export const edgeProgress = (states: SegmentQueryState[], side: SegmentSide) => {
  const edge = states[side === 'past' ? 0 : states.length - 1];

  return edge?.lastPageParam ?? 0;
};

// Messages on the days strictly between two dates, whichever way round they are given.
export const rowsBetween = (messagesByDay: Record<string, CalendarMessage[]>, a: string, b: string) => {
  const [from, to] = a <= b ? [a, b] : [b, a];

  return Object.keys(messagesByDay).reduce((total, day) => (day > from && day < to ? total + messagesByDay[day].length : total), 0);
};

// Which open queries have dropped pages that the viewport is now close to. The gap sits between the
// last loaded day of the past side and the first loaded day of the future side, so the distance to it
// is counted in loaded messages from the first day visible in the viewport.
export const queriesToRefill = (states: SegmentQueryState[], messagesByDay: Record<string, CalendarMessage[]>, viewDate: string | null) => {
  if (!viewDate) return [];

  const refill: number[] = [];

  states.forEach((state, index) => {
    if (!state.hasPreviousPage || state.isFetching || !state.isSuccess) return;

    // The gap lies after the last loaded day of the past side and before the first loaded day of the future side.
    const gapDate = index === 0 ? state.lastDate : state.firstDate;

    if (gapDate === undefined) return;

    const distance = viewDate === gapDate ? 0 : rowsBetween(messagesByDay, viewDate, gapDate);

    if (distance < REFILL_ROWS) refill.push(index);
  });

  return refill;
};

// A new segment directly beyond the edge. With empty dates shown every date is a row, so a segment
// with no messages still adds to the list and there is nothing to be gained from making it longer.
export const nextSegment = (edge: BoundedSegment, side: SegmentSide): BoundedSegment =>
  side === 'past'
    ? { from: addDaysISO(edge.from, -SEGMENT_DAYS), to: addDaysISO(edge.from, -1), direction: 'older' }
    : { from: addDaysISO(edge.to, 1), to: addDaysISO(edge.to, SEGMENT_DAYS), direction: 'newer' };

export const planEdgeStep = (states: SegmentQueryState[], side: SegmentSide): EdgeAction => {
  const edge = states[side === 'past' ? 0 : states.length - 1];

  if (!edge || !edge.isSuccess || edge.isFetching) return { type: 'wait' };
  // Another page only extends the list outwards when the segment goes that way. A newer segment at the past
  // edge (the older one was removed) has loaded its earliest dates first, so its next page adds later dates.
  if (edge.hasNextPage && goesOutwards(edge.segment, side)) return { type: 'fetchNextPage' };
  // An open ended edge has been paged to its last message, so that side is finished.
  if (!isBounded(edge.segment)) return { type: 'end' };

  // Closed segments walk through time and never give up: each request adds one and waits for the next scroll.
  return { type: 'append', segment: nextSegment(edge.segment, side) };
};

// Dropping the far end keeps the number of mounted days bounded. It is only allowed when the viewport
// is well clear of the segment that goes, otherwise the user would watch content vanish.
const trimOpposite = (segments: BoundedSegment[], side: SegmentSide, viewDate: CalendarMonthString | null) => {
  if (!viewDate) return null;

  if (side === 'past') {
    const last = segments[segments.length - 1];

    return viewDate < addDaysISO(last.from, -SEGMENT_DAYS) ? segments.slice(0, -1) : null;
  }

  const [first] = segments;

  return viewDate > addDaysISO(first.to, SEGMENT_DAYS) ? segments.slice(1) : null;
};

// Returns the same array when nothing changed, so it is safe to apply the same append twice.
export const appendSegment = (
  segments: CalendarSegment[],
  side: SegmentSide,
  segment: CalendarSegment,
  viewDate: CalendarMonthString | null
): CalendarSegment[] => {
  // Open ended lists never grow by segment.
  if (!segments.every(isBounded) || !isBounded(segment)) return segments;

  const closed = segments as BoundedSegment[];
  const edge = side === 'past' ? closed[0] : closed[closed.length - 1];
  const isAdjacent = side === 'past' ? addDaysISO(edge.from, -1) === segment.to : addDaysISO(edge.to, 1) === segment.from;

  if (!isAdjacent) return segments;

  let kept = closed;

  if (closed.length >= MAX_SEGMENTS) {
    const trimmed = trimOpposite(closed, side, viewDate);

    if (!trimmed) return segments;
    kept = trimmed;
  }

  return side === 'past' ? [segment, ...kept] : [...kept, segment];
};

const pageMessages = (page: unknown): CalendarMessage[] => {
  const candidate = page as { messages?: CalendarMessage[]; days?: CalendarData[] } | CalendarMessage[] | undefined;

  if (Array.isArray(candidate)) return candidate;
  if (Array.isArray(candidate?.messages)) return candidate.messages;
  if (Array.isArray(candidate?.days)) return candidate.days.flatMap((day) => day.messages);

  return [];
};

export const countRows = (pages: unknown[] | undefined) => (pages ?? []).reduce<number>((total, page) => total + pageMessages(page).length, 0);

export const loadedDateRange = (pages: unknown[] | undefined) => {
  let firstDate: string | undefined;
  let lastDate: string | undefined;

  (pages ?? []).forEach((page) =>
    pageMessages(page).forEach((message) => {
      const date = toDateOnly(message.sendDateLocalized);

      if (firstDate === undefined || date < firstDate) firstDate = date;
      if (lastDate === undefined || date > lastDate) lastDate = date;
    })
  );

  return { firstDate, lastDate };
};

export const buildMessagesByDay = (pagesPerSegment: Array<unknown[] | undefined>) => {
  const byDay: Record<string, CalendarMessage[]> = {};

  pagesPerSegment.forEach((pages) => {
    (pages ?? []).forEach((page) => {
      pageMessages(page).forEach((message) => {
        const iso = toDateOnly(message.sendDateLocalized);

        if (!byDay[iso]) byDay[iso] = [];

        const existing = byDay[iso].findIndex((m) => m.id === message.id);

        // The latest copy wins so refreshed fields (e.g. deliveryStatus.recipients) are never stale.
        if (existing === -1) byDay[iso].push(message);
        else byDay[iso][existing] = message;
      });
    });
  });

  Object.values(byDay).forEach((messages) => messages.sort((a, b) => a.sendDate.localeCompare(b.sendDate)));

  return byDay;
};

// The messages in the last page held and the date among them that is nearest the focus date: the latest
// for an older segment, whose pages run newest first, and the earliest for a newer one.
export const lastPageInfo = (pages: unknown[] | undefined, direction: CalendarSegment['direction']) => {
  const last = pages && pages.length > 0 ? pageMessages(pages[pages.length - 1]) : [];
  const dates = last.map((message) => toDateOnly(message.sendDateLocalized)).sort();

  return {
    lastPageRows: last.length,
    lastPageNearDate: dates.length === 0 ? undefined : direction === 'older' ? dates[dates.length - 1] : dates[0],
  };
};

export type TrimPlan = { index: number; kind: 'page' | 'segment' };

// With empty dates shown, closed segments keep every page they have loaded, so a busy account could hold far
// more than the number of segments suggests. Over the budget, the end of the list opposite to the one that
// just grew gives up its outermost page, and the segment once it has none left. What stays is always a run of
// dates from the edge nearest the viewport, which a segment with more pages already is, so nothing needs a
// placeholder. Nothing within PROTECT_ROWS messages of the viewport goes, so the budget can be exceeded
// for a while in a dense stretch.
export const planTrim = (
  states: SegmentQueryState[],
  grewSide: SegmentSide | null,
  messagesByDay: Record<string, CalendarMessage[]>,
  viewDate: string | null
): TrimPlan | null => {
  if (!grewSide || !viewDate || states.length < 2 || !states.every((state) => isBounded(state.segment))) return null;
  if (states.reduce((total, state) => total + state.rowCount, 0) <= ROW_BUDGET) return null;

  const index = grewSide === 'past' ? states.length - 1 : 0;
  const state = states[index];

  if (!state.isSuccess || state.isFetching || !state.pageCount) return null;

  const { segment } = state;
  // The end that gives up pages is the opposite one to the side that grew.
  const trimmedSide: SegmentSide = grewSide === 'past' ? 'future' : 'past';
  // The last page held is only the outer end of the list when the segment goes outwards on that side.
  // Otherwise it is next to the segment beside it, and taking it would leave a hole in the dates.
  const canTrimPage = goesOutwards(segment, trimmedSide) && state.pageCount > 1 && state.rowCount > 0;
  // The loaded date nearest the viewport that would go: the last page's nearest date, or for a whole
  // segment the end of it that faces the viewport. A segment without messages has only its own edge.
  const facing = trimmedSide === 'past' ? state.lastDate : state.firstDate;
  const edgeDate = trimmedSide === 'past' ? segment.to : segment.from;
  const near = canTrimPage ? state.lastPageNearDate : (facing ?? edgeDate);

  if (near === null || near === undefined) return null;

  const viewerIsBeyond = trimmedSide === 'past' ? viewDate <= near : viewDate >= near;

  if (viewerIsBeyond || rowsBetween(messagesByDay, viewDate, near) < PROTECT_ROWS) return null;

  return { index, kind: canTrimPage ? 'page' : 'segment' };
};

const dayNumber = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number);

  return Math.round(Date.UTC(year, month - 1, day) / 86400000);
};

// The date in the list that is nearest to the target, on either side. A tie goes to the later date. Used to
// stay on the same date, or as near to it as the list allows, when the rows change under the viewport.
export const closestDate = (dates: string[], target: string): string | undefined => {
  const targetDay = dayNumber(target);
  let best: string | undefined;
  let bestDistance = Infinity;

  dates.forEach((date) => {
    const distance = Math.abs(dayNumber(date) - targetDay);

    if (distance < bestDistance || (distance === bestDistance && best !== undefined && date > best)) {
      best = date;
      bestDistance = distance;
    }
  });

  return best;
};
